import type { ReactNode } from 'react';
import { HeroMeta, PageHero } from '@/components/ui';
import { COMPANY, SITE } from '@/content/company';
import { useScrollSpy } from './useScrollSpy';
import styles from './LegalPage.module.css';

interface LegalPageProps {
  eyebrow: string;
  title: string;
  /** Opening paragraph, above the first numbered clause. */
  intro: ReactNode;
  /** Short contents labels, in document order. Index n maps to `sec-{n+1}`. */
  toc: readonly string[];
  children: ReactNode;
}

/**
 * Shared shell for the three legal pages.
 *
 * The design's contents list was a column of click-handling `div`s. Here they
 * are real `#sec-n` anchors inside a labelled `nav`, so they work with the
 * keyboard, work with JavaScript off, and can be opened in a new tab. The
 * scroll-spy highlight is layered on top rather than being the mechanism.
 */
export function LegalPage({ eyebrow, title, intro, toc, children }: LegalPageProps) {
  const active = useScrollSpy(toc.length);

  return (
    <>
      <PageHero
        variant="legal"
        eyebrow={eyebrow}
        title={title}
        lead={
          <HeroMeta>
            Last updated {SITE.legalLastUpdated} · {COMPANY.legalName}, IČO {COMPANY.ico}
          </HeroMeta>
        }
      />

      <div className={styles.body}>
        <nav className={styles.toc} aria-labelledby="toc-heading">
          <h2 id="toc-heading" className={styles.tocHeading}>
            Contents
          </h2>
          <ul className={styles.tocList}>
            {toc.map((label, index) => {
              const id = `sec-${index + 1}`;
              // Before the first measurement, treat the opening clause as current.
              const isActive = active === null ? index === 0 : active === id;
              return (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    className={styles.tocLink}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    {label}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        <article className={`${styles.article} sk-prose-link`}>
          <p className={styles.intro}>{intro}</p>
          {children}
        </article>
      </div>
    </>
  );
}

/**
 * A numbered clause. `id` is the anchor target; `data-sec` is what the
 * scroll-spy measures against.
 */
export function Clause({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  const id = `sec-${n}`;
  return (
    <>
      <h2 id={id} data-sec={id}>
        {n}. {title}
      </h2>
      {children}
    </>
  );
}
